<template>
  <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="onSubmit">
    <v-text-field
      v-model="form.name"
      :rules="rules.name"
      :value="data.name"
      label="Название"
    />
    <custom-select
      class="mt-2"
      :list="informersList"
      :data="data.informers"
      :search-is-loading="isLoading"
      :rules="rules.select"
      label="Выберите информеры"
      search-label="Поиск информера"
      search
      small-chips
      multiple
      chips
      item-value="id"
      item-text="name"
      @change="onChangeInformer"
      @search="debouncedInformerSearch"
    />
    <div>
      <div v-for="(condition, key) in conditions" :key="key">
        <div class="d-flex flex-row justify-space-between">
          <div>{{ BETWEEN_CONDITIONS[condition.betweenCondition] }}</div>
          <div>
            <v-btn
              :disabled="key === conditions.length - 1"
              icon
              @click="swapConditions(key, key + 1)"
            >
              <v-icon>keyboard_arrow_down</v-icon>
            </v-btn>
            <v-btn :disabled="key === 0" icon @click="swapConditions(key, key - 1)">
              <v-icon>keyboard_arrow_up</v-icon>
            </v-btn>
            <v-btn v-if="conditions.length !== 1" icon @click="removeCondition(key)">
              <v-icon small color="red">clear</v-icon>
            </v-btn>
          </div>
        </div>
        <v-select
          v-model="condition.variable"
          :rules="rules.select"
          label="Погода"
          :items="WEATHER_SELECT"
        />
        <v-select
          v-model="condition.condition"
          :rules="rules.select"
          label="Оператор"
          :items="CONDITION_SELECT"
        />
        <v-text-field
          v-model="condition.value"
          :rules="rules.fill"
          :label="`Значение ${unit(condition)}`"
        />
        <custom-select
          :list="conditionInformers"
          :search-is-loading="isLoading"
          :rules="rules.select"
          :data="condition.informers"
          label="Выберите информеры для условия"
          search-label="Поиск информера"
          search
          small-chips
          multiple
          chips
          item-value="id"
          item-text="name"
          @change="(informers) => onChangeConditionInformer(informers, key)"
          @search="debouncedInformerSearch"
        />
        <v-select
          v-model="condition.minHours"
          :rules="rules.select"
          :items="PERIOD_SELECT"
          label="Начало прогноза"
        />
        <v-select
          v-model="condition.maxHours"
          :rules="rules.select"
          :items="lastPeriodItems(condition)"
          label="Максимальный прогноз"
        />
      </div>
      <a href="#" @click.prevent="addCondition('AND')">Добавить "И" условие</a>
      <br>
      <a href="#" @click.prevent="addCondition('AND()')">Добавить "И" условие под скобками</a>
      <br>
      <a href="#" @click.prevent="addCondition('OR')">Добавить "ИЛИ" условие</a>
      <br>
      <a href="#" @click.prevent="addCondition('OR()')">Добавить "ИЛИ" условие под скобками</a>
      <br>
      <v-select
        v-model="months"
        label="Месяц/Время года"
        multiple
        chips
        small-chips
        :items="MONTHS_CONDITIONS"
        hide-details
        class="mb-1"
      />
      <a href="#" @click.prevent="selectMonths(null)">Весь год</a>
      <a href="#" class="ml-1" @click.prevent="selectMonths([12, 1, 2])">Зима</a>
      <a href="#" class="ml-1" @click.prevent="selectMonths([3, 4, 5])">Весна</a>
      <a href="#" class="ml-1" @click.prevent="selectMonths([6, 7, 8])">Лето</a>
      <a href="#" class="ml-1" @click.prevent="selectMonths([9, 10, 11])">Осень</a>
      <div>
        Ваше условие выглядит так:
        <p class="mb-0">{{ fullCondition }}</p>
      </div>
      <h4>ТО</h4>
      <!--
      <v-radio-group row>
        <v-radio label="Вывести сообщение" />
        <v-radio label="Отправить СМС" />
      </v-radio-group>
      -->
      <v-select
        v-if="!customMessage"
        v-model="form.message"
        :rules="rules.select"
        :value="data.message"
        label="Сообщение"
        :items="MESSAGES"
      />
      <v-text-field
        v-else
        v-model="form.message"
        :rules="rules.fill"
        :value="data.message"
        label="Сообщение"
      />
      <a href="#" @click.prevent="customMessage = !customMessage">
        <span
          v-text="customMessage ? 'Выбрать сообщение из списка' : 'Ввести произвольное сообщение'"
        />
      </a>

      <v-btn class="primary float-right mt-4" @click="onSubmit">
        Сохранить
      </v-btn>
    </div>
  </v-form>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import debounce from 'lodash/debounce';
import CustomSelect from '@/components/ui/custom-select/Index.vue';
import {
  BETWEEN_CONDITIONS,
  WEATHER_SELECT,
  MONTHS_CONDITIONS,
  CONDITION_SELECT,
  PERIOD_SELECT,
  UNITS,
  MESSAGES,
  getStringFromConditions,
  createDefaultCondition,
  getConditionsFromString,
} from '@/utils/condition-parser';

const createDefaultForm = () => ({
  name: '',
  informers: [],
  condition: '',
  message: '',
});

const createDefaultRules = () => ({
  name: [v => !!v || 'Введите название критерия'],
  dangerousInformerId: [v => !!v || 'Выберите информер'],
  select: [v => !!v || 'Выберите элемент из списка'],
  fill: [v => !!v || 'Заполните поле'],
});

export default {
  name: 'dangerous-criteria-form',
  components: {
    CustomSelect,
  },
  props: {
    data: {
      type: Object,
      default: createDefaultForm,
    },
    isEdit: Boolean,
  },
  data: () => ({
    valid: true,
    rules: createDefaultRules(),
    form: createDefaultForm(),
    isLoading: false,
    conditions: [
      createDefaultCondition(),
    ],
    months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    customMessage: false,
  }),
  computed: {
    ...mapState('dangerousInformers', {
      informersList: 'fullList',
    }),

    unit() {
      return (c) => UNITS[c.variable];
    },

    lastPeriodItems() {
      return (condition) => PERIOD_SELECT
        .filter(_ => parseInt(condition.minHours, 10) <= parseInt(_.value, 10));
    },

    fullCondition() {
      return getStringFromConditions(this.conditions, this.months, true);
    },

    conditionInformers() {
      const formInformers = this.form.informers;
      const dataInformers = this.data.informers;

      const { informersList } = this;
      if (formInformers && dataInformers) {
        return informersList.filter(_ => formInformers.includes(_.id));
      }

      if (dataInformers) {
        return informersList.filter(_ => this.data.informers.map(__ => __.id).includes(_.id));
      }

      return [];
    },
  },
  watch: {
    data: {
      immediate: true,
      deep: true,
      handler(value) {
        this.form = {
          ...this.form,
          ...JSON.parse(JSON.stringify(value)),
        };
        const { conditions, months } = getConditionsFromString(this.form.condition);
        if (conditions) this.conditions = conditions;
        if (months) this.months = months;
        this.customMessage = !!(this.form.message && !MESSAGES.includes(this.form.message));
      },
    },
    conditions: {
      deep: true,
      handler(conditions) {
        conditions.forEach((_, k) => {
          if (_.maxHours !== null && _.minHours > _.maxHours) this.conditions[k].maxHours = null;
        });
      },
    },
  },
  created() {
    this.form = {
      ...this.form,
      ...JSON.parse(JSON.stringify(this.data)),
    };

    this.WEATHER_SELECT = WEATHER_SELECT;
    this.CONDITION_SELECT = CONDITION_SELECT;
    this.BETWEEN_CONDITIONS = BETWEEN_CONDITIONS;
    this.MONTHS_CONDITIONS = MONTHS_CONDITIONS;
    this.MESSAGES = MESSAGES;
    this.PERIOD_SELECT = PERIOD_SELECT;

    this.debouncedInformerSearch = debounce(this.onInformerSearch, 650);
  },
  methods: {
    ...mapActions('dangerousInformers', {
      getInformers: 'getFullList',
    }),

    onSubmit() {
      if (this.$refs.form.validate()) {
        this.form.condition = getStringFromConditions(this.conditions, this.months, false);
        this.$emit('submit', { form: this.form, isEdit: this.isEdit });
      }
    },

    addCondition(type) {
      this.conditions.push(createDefaultCondition(type));
    },

    removeCondition(key) {
      this.conditions.splice(key, 1);
      this.conditions[0].betweenCondition = undefined;
    },

    swapConditions(i, j) {
      const temp = this.conditions[i];
      this.$set(this.conditions, i, this.conditions[j]);
      this.$set(this.conditions, j, temp);

      const fixBetween = (key1, key2) => {
        if (this.conditions[key1].betweenCondition === undefined) {
          this.$set(this.conditions[key1], 'betweenCondition', this.conditions[key2].betweenCondition);
          this.$set(this.conditions[key2], 'betweenCondition', undefined);
        }
      };

      if (i > j) fixBetween(i, j); else fixBetween(j, i);
    },

    selectMonths(months) {
      if (months === null) this.months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
      else this.months = months;
    },

    async onInformerSearch(query) {
      this.isLoading = true;
      await this.getInformers({ query });
      this.isLoading = false;
    },

    onChangeConditionInformer(informers, index) {
      if (informers === null || informers[0] === undefined) return;
      this.conditions[index].informers = informers;
    },

    onChangeInformer(informers) {
      this.form.informers = informers;
    },
  },
};
</script>

<style scoped>

</style>
